import type { ChangeValue, EntityAttributeChange, Maybe } from "types/generated";
import { EntityChangeType } from "types/generated";

import type {
  RunCodeChangesDiffChunk,
  RunCodeChangesDiffLineType,
  RunCodeChangesGroupNode,
  RunCodeChangesJsonToken,
  RunCodeChangesNode,
  RunCodeChangesResolvedValue,
  RunCodeChangesTokenizedDiffLine,
} from "./types";

/** Grouping requires path segments — API escapes literal dots with backslash (e.g. K8s annotation keys) */
export const splitAttributePath = (path: string): string[] => {
  const parts = path.split(".");
  const result: string[] = [];

  for (let i = 0; i < parts.length; i++) {
    let part = parts[i];

    while (part.endsWith("\\")) {
      i++;
      part += "." + parts[i];
    }

    result.push(part.replaceAll("\\.", "."));
  }

  return result;
};

/** GQL query aliases `value` to stringVal/floatVal/boolVal — runtime has aliased names only */
export const extractAttributeValue = (
  changeValue: Maybe<ChangeValue>
): RunCodeChangesResolvedValue | undefined => {
  if (!changeValue || !changeValue.__typename) return undefined;

  if ("stringVal" in changeValue && typeof changeValue.stringVal === "string") {
    return { type: "string", value: changeValue.stringVal };
  }

  if ("floatVal" in changeValue && typeof changeValue.floatVal === "number") {
    return { type: "number", value: changeValue.floatVal };
  }

  if ("boolVal" in changeValue && typeof changeValue.boolVal === "boolean") {
    return { type: "boolean", value: changeValue.boolVal };
  }

  return undefined;
};

export const buildAttributeTree = (items: EntityAttributeChange[]): RunCodeChangesNode[] => {
  const root: RunCodeChangesNode[] = [];

  for (const item of items) {
    const segments = splitAttributePath(item.path);
    let children = root;

    for (let i = 0; i < segments.length - 1; i++) {
      const key = segments[i];
      const id = segments.slice(0, i + 1).join(".");
      let group = children.find(
        (n): n is RunCodeChangesGroupNode => n.kind === "group" && n.key === key
      );

      if (!group) {
        group = { kind: "group", key, children: [], id };
        children.push(group);
      }

      children = group.children;
    }

    children.push({
      kind: "leaf",
      key: segments[segments.length - 1],
      item,
      id: item.path,
    });
  }

  return root;
};

export const isArrayGroup = (children: RunCodeChangesNode[]): boolean =>
  children.length > 0 && children.every((child) => !isNaN(Number(child.key)));

export const getGroupChangeType = (
  children: RunCodeChangesNode[],
  found?: EntityChangeType
): EntityChangeType | undefined => {
  for (const node of children) {
    const type =
      node.kind === "leaf"
        ? node.item.changeForPhase.metadata.type
        : getGroupChangeType(node.children, found);

    if (!type || type === EntityChangeType.Noop) {
      continue;
    }
    if (!found) {
      found = type;
    }
    if (found !== type) {
      return EntityChangeType.Change;
    }
  }

  return found;
};

export const tokenizeJsonLines = (
  text: string,
  type: RunCodeChangesDiffLineType
): RunCodeChangesTokenizedDiffLine[] =>
  text.split("\n").map((line) => ({ tokens: tokenizeJsonLine(line), type }));

export const prettifyJson = (value: string): string => {
  try {
    return JSON.stringify(JSON.parse(value), null, 2);
  } catch {
    return value;
  }
};

const JSON_TOKEN_PATTERN =
  /("(?:[^"\\]|\\.)*")\s*(:?)|(-?\d+(?:\.\d+)?(?:[eE][+-]?\d+)?)\b|\b(true|false)\b|\b(null)\b/g;

export const tokenizeJsonLine = (line: string): RunCodeChangesJsonToken[] => {
  const tokens: RunCodeChangesJsonToken[] = [];
  let lastIndex = 0;

  for (const match of line.matchAll(JSON_TOKEN_PATTERN)) {
    const index = match.index ?? 0;

    if (index > lastIndex) {
      tokens.push({ text: line.slice(lastIndex, index), type: "punctuation" });
    }

    if (match[1] !== undefined) {
      const isKey = match[2] === ":";
      tokens.push({ text: match[1], type: isKey ? "key" : "string" });

      if (isKey) {
        tokens.push({ text: ":", type: "punctuation" });
      }
    } else if (match[3] !== undefined) {
      tokens.push({ text: match[0], type: "number" });
    } else if (match[4] !== undefined) {
      tokens.push({ text: match[0], type: "boolean" });
    } else if (match[5] !== undefined) {
      tokens.push({ text: match[0], type: "null" });
    }

    lastIndex = index + match[0].length;
  }

  if (lastIndex < line.length) {
    tokens.push({ text: line.slice(lastIndex), type: "punctuation" });
  }

  return tokens;
};

const MIN_FOLDED_LINES = 4;

export const collapseUnchangedLines = (
  lines: RunCodeChangesTokenizedDiffLine[],
  context = 3
): RunCodeChangesDiffChunk[] => {
  const chunks: RunCodeChangesDiffChunk[] = [];
  let index = 0;

  while (index < lines.length) {
    if (lines[index].type !== "unchanged") {
      chunks.push(lines[index]);
      index++;
      continue;
    }

    let end = index;
    while (end < lines.length && lines[end].type === "unchanged") end++;

    const foldStart = index + (index === 0 ? 0 : context);
    const foldEnd = end - (end === lines.length ? 0 : context);

    if (foldEnd - foldStart < MIN_FOLDED_LINES) {
      chunks.push(...lines.slice(index, end));
    } else {
      chunks.push(
        ...lines.slice(index, foldStart),
        { count: foldEnd - foldStart, lines: lines.slice(foldStart, foldEnd) },
        ...lines.slice(foldEnd, end)
      );
    }

    index = end;
  }

  return chunks;
};
